/**
 * Mock data for supplier drivers (couriers) management.
 *
 * NOTE: There is currently no backend API for supplier drivers.
 * This file provides local mock data so the UI can be fully demoed.
 * Once the API is ready, the pages using this data should be wired
 * to real service calls instead.
 */

export const VEHICLE_TYPES = [
  { value: "bicycle", labelKey: "vehicle_bicycle", icon: "mdi-bike" },
  { value: "motorcycle", labelKey: "vehicle_motorcycle", icon: "mdi-moped" },
  { value: "scooter", labelKey: "vehicle_scooter", icon: "mdi-scooter" },
  { value: "car", labelKey: "vehicle_car", icon: "mdi-car" },
];

// A driver can use more than one type of vehicle (e.g. bicycle and scooter),
// so `vehicle_types` is always an array of VEHICLE_TYPES values.
export const getVehicleTypes = (values) => {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  const resolved = list
    .map((value) => VEHICLE_TYPES.find((v) => v.value === value))
    .filter(Boolean);
  return resolved.length ? resolved : [VEHICLE_TYPES[0]];
};

let nextDriverId = 9;
export const generateNextDriverId = () => nextDriverId++;

export const generateMockDrivers = () => [
  {
    id: 1,
    name: "Marko",
    lastname: "Marković",
    phone: "+38765123456",
    email: "marko.markovic@example.com",
    vehicle_types: ["motorcycle", "bicycle"],
    active: true,
    note: "",
    created_at: "2026-01-05",
  },
  {
    id: 2,
    name: "Ivana",
    lastname: "Ivić",
    phone: "+38761234567",
    email: "ivana.ivic@example.com",
    vehicle_types: ["bicycle"],
    active: true,
    note: "",
    created_at: "2026-01-12",
  },
  {
    id: 3,
    name: "Stefan",
    lastname: "Petrović",
    phone: "+38762345678",
    email: "stefan.petrovic@example.com",
    vehicle_types: ["car"],
    active: false,
    note: "",
    created_at: "2026-02-01",
  },
  {
    id: 4,
    name: "Amar",
    lastname: "Hodžić",
    phone: "+38763456789",
    email: "amar.hodzic@example.com",
    vehicle_types: ["scooter", "bicycle"],
    active: true,
    note: "",
    created_at: "2026-02-15",
  },
  {
    id: 5,
    name: "Elvira",
    lastname: "Softić",
    phone: "+38764567890",
    email: "elvira.softic@example.com",
    vehicle_types: ["bicycle"],
    active: true,
    note: "",
    created_at: "2026-03-03",
  },
  {
    id: 6,
    name: "Nikola",
    lastname: "Nikolić",
    phone: "+38765678901",
    email: "nikola.nikolic@example.com",
    vehicle_types: ["motorcycle"],
    active: true,
    note: "",
    created_at: "2026-03-20",
  },
  {
    id: 7,
    name: "Dino",
    lastname: "Delić",
    phone: "+38766789012",
    email: "dino.delic@example.com",
    vehicle_types: ["car", "motorcycle"],
    active: true,
    note: "",
    created_at: "2026-04-02",
  },
  {
    id: 8,
    name: "Sanja",
    lastname: "Savić",
    phone: "+38767890123",
    email: "sanja.savic@example.com",
    vehicle_types: ["scooter"],
    active: false,
    note: "",
    created_at: "2026-04-18",
  },
];

// Deterministic pseudo-random generator seeded by a number, so stats
// stay stable across re-renders without needing a real backend yet.
const seededRandom = (seed) => {
  const x = Math.sin(seed * 9973) * 10000;
  return x - Math.floor(x);
};

/**
 * Generates mock delivery statistics for the given list of drivers.
 * Kept as a pure function so a real API response can later be mapped
 * into the same shape without touching the pages that consume it.
 */
export const generateMockDriverStats = (drivers) => {
  return drivers.map((driver) => {
    const r1 = seededRandom(driver.id + 1);
    const r2 = seededRandom(driver.id + 2);
    const r3 = seededRandom(driver.id + 3);
    const r4 = seededRandom(driver.id + 4);
    const r5 = seededRandom(driver.id + 5);

    const completed = driver.active
      ? Math.round(20 + r1 * 180)
      : Math.round(r1 * 20);
    const canceled = Math.round(completed * (0.02 + r2 * 0.08));
    const distance = Math.round(completed * (3 + r3 * 9) * 10) / 10;
    const avgOrderValue = 12 + r4 * 28;
    const totalValue = Math.round(completed * avgOrderValue * 100) / 100;
    const rating = Math.min(Math.round((3.5 + r5 * 1.5) * 10) / 10, 5);

    return {
      driver_id: driver.id,
      deliveries_completed: completed,
      deliveries_canceled: canceled,
      total_distance_km: distance,
      total_delivered_value: totalValue,
      average_rating: rating,
      last_delivery_date: driver.active
        ? new Date(Date.now() - Math.round(r2 * 6) * 86400000)
            .toISOString()
            .split("T")[0]
        : null,
    };
  });
};
